/**
 * Case studies for /case-studies.
 *
 * Every entry below is a PLACEHOLDER: an illustrative example of the kind of engagement
 * inaraX runs, not a real client or a real result. `illustrative: true` makes the pages say
 * so on screen, and the route is noindexed while any placeholder is listed (see
 * `HAS_PLACEHOLDER_CASE_STUDIES`). Replace an entry with a real, client-approved study and
 * set `illustrative: false`; once none are left, the pages index normally.
 */

export const CASE_STUDY_CATEGORIES = [
  "Enterprise AI Upskilling",
  "Role-Based Training",
  "Custom Programs",
] as const;

export type CaseStudyCategory = (typeof CASE_STUDY_CATEGORIES)[number];

export type CaseStudy = {
  slug: string;
  category: CaseStudyCategory;
  industry: string;
  /** Anonymized description of the client, e.g. "A regional retail bank". */
  client: string;
  /** Outcome-led headline. */
  title: string;
  summary: string;
  /** Two or three headline numbers shown on the card and the detail page. */
  metrics: readonly { value: string; label: string }[];
  challenge: string;
  approach: readonly { step: string; detail: string }[];
  results: readonly string[];
  illustrative: boolean;
};

export const CASE_STUDIES: readonly CaseStudy[] = [
  {
    slug: "regional-bank-ai-readiness",
    category: "Enterprise AI Upskilling",
    industry: "Banking & Financial Services",
    client: "A regional retail bank",
    title: "Getting 1,200 branch and operations staff AI-ready in one quarter",
    summary:
      "A bank-wide AI capability program that started from an assessment of every employee rather than a single mandatory course.",
    metrics: [
      { value: "1,200", label: "employees assessed" },
      { value: "+41%", label: "average capability gain" },
      { value: "12 wks", label: "from kickoff to rollout" },
    ],
    challenge:
      "Leadership wanted AI adoption across branches and back-office teams, but staff ranged from daily ChatGPT users to people who had never tried a generative AI tool. A single company-wide course had low completion and no way to show what people could actually do afterwards.",
    approach: [
      { step: "Assess", detail: "Every employee took a short AI capability assessment, giving L&D a baseline by team and role." },
      { step: "Personalize", detail: "inaraX built paths for branch, operations, risk and management roles, each starting at the right level." },
      { step: "Practice", detail: "Exercises used anonymized, banking-specific scenarios such as customer correspondence and policy summaries." },
      { step: "Validate", detail: "Experts reviewed capstone tasks before skills were reported as verified." },
      { step: "Measure", detail: "L&D tracked progress and re-assessment results by branch on one dashboard." },
    ],
    results: [
      "A role-by-role picture of AI capability for the first time",
      "Re-assessment showed measurable gains in every role group",
      "Managers used the dashboard to target support at lagging branches",
    ],
    illustrative: true,
  },
  {
    slug: "telecom-sales-role-based-training",
    category: "Role-Based Training",
    industry: "Telecommunications",
    client: "A national telecom operator",
    title: "Role-specific AI training for sales, support and marketing teams",
    summary:
      "Three functions, three very different uses of AI, one platform that gave each team its own learning path.",
    metrics: [
      { value: "3", label: "role tracks launched" },
      { value: "87%", label: "path completion" },
      { value: "-30%", label: "time to first AI-assisted output" },
    ],
    challenge:
      "Sales, customer support and marketing all wanted AI skills, but generic training spent most of its time on use cases none of them had. Completion dropped after the first module.",
    approach: [
      { step: "Assess", detail: "Placement by role and current skill level, so experienced users skipped the basics." },
      { step: "Personalize", detail: "Separate tracks for prospect research, support responses and campaign content." },
      { step: "Practice", detail: "Practice tasks drawn from each team's real workflows, with an AI tutor on hand." },
      { step: "Validate", detail: "Expert review of a final role-specific project." },
      { step: "Measure", detail: "Completion and assessment results reported per team." },
    ],
    results: [
      "Completion stayed high because no one sat through irrelevant material",
      "Teams shipped AI-assisted work in their first weeks",
      "L&D reused the tracks for new joiners",
    ],
    illustrative: true,
  },
  {
    slug: "healthcare-custom-program",
    category: "Custom Programs",
    industry: "Healthcare",
    client: "A private hospital group",
    title: "A custom AI program for clinical and administrative leaders",
    summary:
      "Content co-built around the group's own policies and data rules, then personalized for clinical and non-clinical roles.",
    metrics: [
      { value: "250", label: "leaders enrolled" },
      { value: "100%", label: "content reviewed by experts" },
      { value: "4", label: "departments onboarded" },
    ],
    challenge:
      "The group needed leaders to understand AI's opportunities and limits in a regulated setting. Off-the-shelf courses ignored patient-data rules and used examples from other industries.",
    approach: [
      { step: "Assess", detail: "Baseline assessment of AI literacy across clinical and administrative leadership." },
      { step: "Personalize", detail: "Co-built modules on governance, safe use and process improvement, adapted per role." },
      { step: "Practice", detail: "Scenarios built on anonymized hospital workflows rather than generic examples." },
      { step: "Validate", detail: "Healthcare and AI experts reviewed every module and final assessment." },
      { step: "Measure", detail: "Department-level reporting for the executive sponsor." },
    ],
    results: [
      "A shared language for AI decisions across departments",
      "Clear governance guidance leaders could apply immediately",
      "A repeatable program for the next leadership cohort",
    ],
    illustrative: true,
  },
  {
    slug: "public-sector-ai-literacy",
    category: "Enterprise AI Upskilling",
    industry: "Public Sector",
    client: "A government agency",
    title: "AI literacy at scale for a distributed public-sector workforce",
    summary:
      "An agency-wide program that met employees where they were, across offices, roles and levels of digital confidence.",
    metrics: [
      { value: "3,000+", label: "employees reached" },
      { value: "+35%", label: "assessed AI literacy" },
      { value: "6", label: "regional offices" },
    ],
    challenge:
      "The agency had a national mandate to build AI literacy but a workforce spread across regions with very different starting points and limited time for training.",
    approach: [
      { step: "Assess", detail: "A short, accessible assessment set each person's starting point." },
      { step: "Personalize", detail: "Paths adapted to role and confidence, in short sessions that fit around work." },
      { step: "Practice", detail: "Exercises built on public-service tasks such as drafting, summarizing and research." },
      { step: "Validate", detail: "Expert-reviewed assessments for staff moving into AI champion roles." },
      { step: "Measure", detail: "Regional dashboards for program leads." },
    ],
    results: [
      "Consistent baseline AI literacy across regions",
      "A network of validated AI champions in every office",
      "Reporting that met the mandate's requirements",
    ],
    illustrative: true,
  },
  {
    slug: "software-company-engineering-track",
    category: "Role-Based Training",
    industry: "Software & Technology",
    client: "A mid-size software company",
    title: "Moving engineering teams from AI experiments to everyday practice",
    summary:
      "An engineering-specific track that skipped the basics for advanced users and validated real work, not quiz scores.",
    metrics: [
      { value: "180", label: "engineers trained" },
      { value: "2x", label: "teams using AI in daily workflow" },
      { value: "90%", label: "projects passed expert review" },
    ],
    challenge:
      "Some engineers were already deep into AI tooling while others avoided it. Leadership wanted consistent, responsible use, without boring the experts or losing the beginners.",
    approach: [
      { step: "Assess", detail: "Hands-on assessment placed each engineer at the right level." },
      { step: "Personalize", detail: "Paths for backend, frontend, QA and engineering managers." },
      { step: "Practice", detail: "Coding and review tasks based on the company's own stack." },
      { step: "Validate", detail: "Senior practitioners reviewed final projects." },
      { step: "Measure", detail: "Adoption and project outcomes tracked by team." },
    ],
    results: [
      "Advanced engineers moved straight to advanced material",
      "Teams adopted shared practices for AI-assisted development",
      "Managers could see which teams needed more support",
    ],
    illustrative: true,
  },
  {
    slug: "retail-custom-onboarding",
    category: "Custom Programs",
    industry: "Retail & E-commerce",
    client: "A multi-brand retailer",
    title: "Building AI into onboarding for head-office and store management",
    summary:
      "A custom AI module built into onboarding, so every new manager starts with the same validated foundation.",
    metrics: [
      { value: "400", label: "managers onboarded" },
      { value: "-25%", label: "onboarding time" },
      { value: "5", label: "brands covered" },
    ],
    challenge:
      "Each brand had its own onboarding and none covered AI. New managers picked up tools inconsistently, with no shared standard for safe use.",
    approach: [
      { step: "Assess", detail: "Placement at onboarding identified each new manager's starting point." },
      { step: "Personalize", detail: "Co-built content on merchandising, customer insight and reporting, adapted per brand and role." },
      { step: "Practice", detail: "Exercises on anonymized sales and customer data." },
      { step: "Validate", detail: "Expert-reviewed final task before sign-off." },
      { step: "Measure", detail: "Onboarding progress tracked across brands." },
    ],
    results: [
      "One AI standard across every brand",
      "Faster onboarding for new managers",
      "A program HR now runs for every intake",
    ],
    illustrative: true,
  },
];

export const HAS_PLACEHOLDER_CASE_STUDIES = CASE_STUDIES.some((study) => study.illustrative);

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((study) => study.slug === slug);
}
